import { useEffect, useState } from 'react';
import {
  X,
  Copy,
  Check,
  Loader2,
  ChevronRight,
  Timer,
  DatabaseZap,
  Route,
  Activity,
  Layers3,
} from 'lucide-react';
import { explainQuery } from '@/api/monitoring';
import type { QueryLogEntry, ExplainResult } from '@/api/monitoring';
import { useErrorToast } from '@/lib/notify';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const copyWithFallback = (value: string): boolean => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  };

  const copy = async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch {
      ok = false;
    }

    if (!ok) {
      ok = copyWithFallback(text);
    }

    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button
      onClick={copy}
      className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
    >
      {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
      {copied ? 'Kopyalandı' : 'Kopyala'}
    </button>
  );
}

const BUFFER_LABELS: Record<string, { label: string; desc: string; color: string }> = {
  hit:          { label: 'Cache Hit',      desc: 'Bellekten okunan blok',    color: 'text-emerald-600 bg-emerald-50' },
  read:         { label: 'Physical Read',  desc: 'Diskten okunan blok',      color: 'text-rose-600 bg-rose-50' },
  dirtied:      { label: 'Dirtied',        desc: 'Değiştirilen blok',        color: 'text-amber-600 bg-amber-50' },
  written:      { label: 'Written',        desc: 'Diske yazılan blok',       color: 'text-blue-600 bg-blue-50' },
  temp_read:    { label: 'Temp Read',      desc: 'Geçici disk okuması',      color: 'text-violet-600 bg-violet-50' },
  temp_written: { label: 'Temp Written',   desc: 'Geçici disk yazması',      color: 'text-violet-600 bg-violet-50' },
  io_read:      { label: 'I/O Read ms',    desc: 'Disk okuma süresi',        color: 'text-orange-600 bg-orange-50' },
  io_write:     { label: 'I/O Write ms',   desc: 'Disk yazma süresi',        color: 'text-orange-600 bg-orange-50' },
};

function BufferStats({ buffers }: { buffers: Record<string, number> | null }) {
  if (!buffers) return null;
  const normalized: Record<string, number> = {};

  for (const [rawKey, value] of Object.entries(buffers)) {
    if (value <= 0) continue;
    const key = rawKey.toLowerCase();

    if (key === 'hit' || key.endsWith('_hit')) {
      normalized.hit = (normalized.hit ?? 0) + value;
      continue;
    }
    if (key === 'read' || key.endsWith('_read')) {
      if (key.startsWith('io_') || key.startsWith('temp_')) {
        normalized[key] = (normalized[key] ?? 0) + value;
      } else {
        normalized.read = (normalized.read ?? 0) + value;
      }
      continue;
    }
    if (key === 'dirtied' || key.endsWith('_dirtied')) {
      normalized.dirtied = (normalized.dirtied ?? 0) + value;
      continue;
    }
    if (key === 'written' || key.endsWith('_written')) {
      if (key.startsWith('io_') || key.startsWith('temp_')) {
        normalized[key] = (normalized[key] ?? 0) + value;
      } else {
        normalized.written = (normalized.written ?? 0) + value;
      }
      continue;
    }
    if (key.startsWith('io_') || key.startsWith('temp_')) {
      normalized[key] = (normalized[key] ?? 0) + value;
    }
  }

  const entries = Object.entries(normalized).filter(([, v]) => v > 0);
  if (!entries.length) return null;

  const total = (normalized.hit ?? 0) + (normalized.read ?? 0);
  const hitRate = total > 0 ? ((normalized.hit ?? 0) / total * 100).toFixed(1) : null;

  return (
    <>
      {hitRate !== null && (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ml-2 ${
          parseFloat(hitRate) >= 95 ? 'bg-emerald-50 text-emerald-600' :
          parseFloat(hitRate) >= 80 ? 'bg-amber-50 text-amber-600' :
          'bg-rose-50 text-rose-600'
        }`}>
          Cache hit: %{hitRate}
        </span>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        {entries.map(([key, value]) => {
          const meta = BUFFER_LABELS[key];
          if (!meta) return null;
          return (
            <div key={key} className={`rounded-lg px-3 py-2 ${meta.color}`}>
              <p className="text-xs font-medium">{meta.label}</p>
              <p className="text-lg font-bold leading-none mt-0.5">
                {key.startsWith('io_') ? `${value.toFixed(2)}` : value.toLocaleString('tr-TR')}
              </p>
              <p className="text-[10px] opacity-70 mt-0.5">{meta.desc}</p>
            </div>
          );
        })}
      </div>
    </>
  );
}

function MetricCard({
  icon,
  label,
  value,
  tone = 'slate',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: 'slate' | 'blue' | 'emerald' | 'amber';
}) {
  const toneClass =
    tone === 'blue'
      ? 'bg-blue-50 text-blue-700 border-blue-100'
      : tone === 'emerald'
        ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
        : tone === 'amber'
          ? 'bg-amber-50 text-amber-700 border-amber-100'
          : 'bg-slate-50 text-slate-700 border-slate-100';

  return (
    <div className={`rounded-xl border px-3 py-2 ${toneClass}`}>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide">
        {icon}
        <span>{label}</span>
      </div>
      <p className="mt-1 text-base font-bold leading-none">{value}</p>
    </div>
  );
}

interface Props {
  entry: QueryLogEntry;
  onClose: () => void;
}

function formatMetric(value: number | null, fractionDigits = 2): string {
  if (value === null || Number.isNaN(value)) return '—';
  return value.toLocaleString('tr-TR', { maximumFractionDigits: fractionDigits });
}

export default function QueryDetailModal({ entry, onClose }: Props) {
  const [result, setResult] = useState<ExplainResult | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  useErrorToast(statsError);
  const [planOpen, setPlanOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    setStatsLoading(true);
    setStatsError(null);
    explainQuery(entry.query, entry.params)
      .then(setResult)
      .catch((err: any) => setStatsError(err?.response?.data?.error ?? 'İstatistikler alınamadı'))
      .finally(() => setStatsLoading(false));
  }, [entry.id, entry.query, entry.params]);

  const loadPlan = () => {
    setPlanOpen(p => !p);
  };

  function colorize(line: string) {
    if (/Seq Scan|Parallel Seq Scan/.test(line))      return 'text-rose-500';
    if (/Index (Only )?Scan/.test(line))               return 'text-emerald-600';
    if (/Hash Join|Nested Loop|Merge Join/.test(line)) return 'text-blue-500';
    if (/Sort|Aggregate|Group/.test(line))             return 'text-amber-600';
    if (/Buffers:|I\/O Timings:/.test(line))           return 'text-violet-400 font-semibold';
    if (/cost=/.test(line))                            return 'text-gray-400';
    return 'text-gray-300';
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex flex-col bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono text-gray-400">#{entry.id}</span>
            <span className="text-sm font-medium text-gray-700">{entry.started_at}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              entry.status === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
            }`}>
              {entry.status === 'success' ? 'Başarılı' : 'Hata'}
            </span>
            <span className="text-xs text-gray-400">{entry.duration_ms} ms · {entry.row_count} satır</span>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">

          {/* Sorgu metni */}
          <div className="px-6 py-4 border-b">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sorgu</span>
              <CopyButton text={entry.query} />
            </div>
            <pre className="bg-gray-950 text-gray-100 text-xs font-mono rounded-xl p-4 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">
              {entry.query}
            </pre>
          </div>

          {/* Params */}
          {entry.params && entry.params.length > 0 && (
            <div className="px-6 py-4 border-b">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Parametreler</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {entry.params.map((p, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs font-mono">
                    <span className="text-gray-400">${i + 1}</span>
                    <span className="text-gray-700">{p === null ? 'null' : String(p)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Hata mesajı */}
          {entry.error && (
            <div className="px-6 py-4 border-b">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Hata</span>
              <p className="mt-2 text-sm text-rose-600 bg-rose-50 rounded-xl px-4 py-3">{entry.error}</p>
            </div>
          )}

          {/* Çağrı kaynağı */}
          {(entry.endpoint || entry.source) && (
            <div className="px-6 py-4 border-b">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Çağrı Kaynağı</span>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="rounded-lg border border-gray-200 px-3 py-2">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold">Endpoint</p>
                  <p className="text-xs font-mono text-gray-700 mt-1">
                    {entry.method ? `${entry.method} ` : ''}{entry.endpoint ?? '—'}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 px-3 py-2">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold">Kod Satırı</p>
                  <p className="text-xs font-mono text-gray-700 mt-1">{entry.source ?? '—'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Buffer istatistikleri — otomatik yüklenir */}
          <div className="px-6 py-4 border-b">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sorgu Analiz Özeti</span>
              {result && (
                <span
                  className={`text-[11px] font-semibold px-2 py-1 rounded-full ${
                    result.mode === 'analyze'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-amber-50 text-amber-700'
                  }`}
                >
                  {result.mode === 'analyze' ? 'Gerçek Çalıştırma (ANALYZE)' : 'Tahmini Plan (EXPLAIN)'}
                </span>
              )}
            </div>
            {statsLoading && (
              <div className="flex items-center gap-2 text-gray-400 text-sm mt-3">
                <Loader2 size={14} className="animate-spin" /> Yükleniyor...
              </div>
            )}
            {result && !statsLoading && !statsError && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <MetricCard
                    icon={<Timer size={13} />}
                    label="Sorgu Süresi"
                    value={`${formatMetric(entry.duration_ms, 1)} ms`}
                    tone="blue"
                  />
                  <MetricCard
                    icon={<DatabaseZap size={13} />}
                    label="Dönen Satır"
                    value={entry.row_count.toLocaleString('tr-TR')}
                    tone="emerald"
                  />
                  <MetricCard
                    icon={<Route size={13} />}
                    label="Durum"
                    value={entry.status === 'success' ? 'Başarılı' : 'Hata'}
                    tone={entry.status === 'success' ? 'emerald' : 'amber'}
                  />
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                  <MetricCard
                    icon={<Activity size={13} />}
                    label="Planning Time"
                    value={`${formatMetric(result.stats.planningTimeMs)} ms`}
                  />
                  <MetricCard
                    icon={<Activity size={13} />}
                    label="Execution Time"
                    value={`${formatMetric(result.stats.executionTimeMs)} ms`}
                  />
                  <MetricCard
                    icon={<Layers3 size={13} />}
                    label="Estimated Rows"
                    value={formatMetric(result.stats.estimatedRows, 0)}
                  />
                  <MetricCard
                    icon={<Layers3 size={13} />}
                    label="Actual Rows"
                    value={formatMetric(result.stats.actualRows, 0)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="rounded-xl border border-gray-200 p-3">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Cost Range</p>
                    <p className="text-sm font-mono text-gray-700 mt-1">
                      {formatMetric(result.stats.totalCostStart)} → {formatMetric(result.stats.totalCostEnd)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-gray-200 p-3">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Plan Node Dağılımı</p>
                    {Object.keys(result.stats.nodeCounts).length === 0 ? (
                      <p className="text-xs text-gray-400 mt-1">Node bilgisi bulunamadı.</p>
                    ) : (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {Object.entries(result.stats.nodeCounts).map(([node, count]) => (
                          <span key={node} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-slate-100 text-slate-700 text-[11px]">
                            <span>{node}</span>
                            <span className="font-semibold">{count}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Buffer & I/O</p>
                  <div className="mt-1">
                    <BufferStats buffers={result.buffers} />
                    {(!result.buffers || Object.keys(result.buffers).length === 0) && (
                      <p className="text-xs text-gray-400">Buffer metrikleri bu plan çıktısında bulunamadı.</p>
                    )}
                  </div>
                </div>

                {result.warnings.length > 0 && (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">Notlar</p>
                    <div className="mt-1 space-y-1">
                      {result.warnings.map((warning) => (
                        <p key={warning} className="text-xs text-amber-700">{warning}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Execution plan — collapsible */}
          <div className="px-6 py-4">
            <button
              onClick={loadPlan}
              className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-blue-500 transition-colors"
            >
              <ChevronRight size={14} className={`transition-transform ${planOpen ? 'rotate-90' : ''}`} />
              Execution Plan
            </button>

            {planOpen && (
              <div className="mt-3">
                {statsLoading && (
                  <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
                    <Loader2 size={15} className="animate-spin" /> Plan hesaplanıyor...
                  </div>
                )}
                {result?.plan && (
                  <div className="relative">
                    <div className="absolute right-3 top-3">
                      <CopyButton text={result.plan} />
                    </div>
                    <pre className="bg-gray-950 rounded-xl p-4 pr-24 overflow-x-auto text-xs font-mono leading-relaxed">
                      {result.plan.split('\n').map((line, i) => (
                        <div key={i} className={colorize(line)}>{line}</div>
                      ))}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
